import * as THREE from 'three';

interface CameraControlsLike {
  object: THREE.Camera;
  target: THREE.Vector3;
  update(): void;
}

interface TransitionState {
  startTime: number;
  duration: number;
  startPos: THREE.Vector3;
  targetPos: THREE.Vector3;
  startUp: THREE.Vector3;
  targetUp: THREE.Vector3;
  startTarget: THREE.Vector3;
  targetLook: THREE.Vector3;
}

/** Owns animated camera movement between view presets and focus points. */
export class CameraTransition {
  private state: TransitionState | null = null;

  public cancel(): void {
    this.state = null;
  }

  public start(
    camera: THREE.Camera,
    targetPos: THREE.Vector3,
    targetUp: THREE.Vector3,
    targetLook: THREE.Vector3,
    duration = 320,
    startTarget = new THREE.Vector3(),
  ): void {
    this.state = {
      startTime: performance.now(),
      duration,
      startPos: camera.position.clone(),
      targetPos: targetPos.clone(),
      startUp: camera.up.clone(),
      targetUp: targetUp.clone(),
      startTarget: startTarget.clone(),
      targetLook: targetLook.clone(),
    };
  }

  public update(
    camera: THREE.Camera,
    controls: CameraControlsLike,
    setOrthoFrustum: (halfHeight: number) => void,
    orthoHalfHeightForDistance: (distance: number) => number,
  ): void {
    const state = this.state;
    if (!state) return;
    const elapsed = performance.now() - state.startTime;
    const progress = Math.min(1, elapsed / state.duration);
    const ease = progress < 0.5
      ? 4 * progress * progress * progress
      : 1 - Math.pow(-2 * progress + 2, 3) / 2;
    const currentTarget = new THREE.Vector3().lerpVectors(state.startTarget, state.targetLook, ease);
    const startOffset = new THREE.Vector3().subVectors(state.startPos, state.startTarget);
    const endOffset = new THREE.Vector3().subVectors(state.targetPos, state.targetLook);
    const currentDistance = THREE.MathUtils.lerp(startOffset.length(), endOffset.length(), ease);
    const startDirection = startOffset.clone().normalize();
    const endDirection = endOffset.clone().normalize();

    let currentDirection: THREE.Vector3;
    if (startDirection.dot(endDirection) < -0.999) {
      const rotation = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), Math.PI * ease);
      currentDirection = startDirection.clone().applyQuaternion(rotation);
    } else {
      const qStart = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), startDirection);
      const qEnd = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), endDirection);
      currentDirection = new THREE.Vector3(0, 0, 1).applyQuaternion(qStart.slerp(qEnd, ease));
    }

    camera.position.copy(currentTarget).addScaledVector(currentDirection, currentDistance);
    camera.up.lerpVectors(state.startUp, state.targetUp, ease).normalize();
    controls.object.up.copy(camera.up);
    controls.target.copy(currentTarget);
    camera.lookAt(currentTarget);
    if ((camera as any).isOrthographicCamera) {
      setOrthoFrustum(orthoHalfHeightForDistance(currentDistance));
    }
    controls.update();

    if (progress >= 1) {
      camera.position.copy(state.targetPos);
      camera.up.copy(state.targetUp);
      controls.object.up.copy(camera.up);
      controls.target.copy(state.targetLook);
      camera.lookAt(state.targetLook);
      if ((camera as any).isOrthographicCamera) {
        setOrthoFrustum(orthoHalfHeightForDistance(endOffset.length()));
      }
      controls.update();
      this.state = null;
    }
  }
}
